import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Home() {
  useEffect(() => {
    const targets = document.querySelectorAll('.reveal');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // If user prefers reduced motion, just show them
    if (reduce) {
      targets.forEach(el => el.classList.add('in'));
      return;
    }

    // Check if elements are already in view on load
    const checkInView = () => {
      targets.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
        if (isInView) {
          el.classList.add('in');
        }
      });
    };

    // Check immediately and after a short delay
    checkInView();
    setTimeout(checkInView, 100);

    // Robust IntersectionObserver (triggers a bit earlier)
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -20% 0px', // fire before the element is fully in view
        threshold: 0
      }
    );

    targets.forEach((el) => {
      // Only observe if not already in view
      if (!el.classList.contains('in')) {
        io.observe(el);
      }
    });

    // Absolute fallback: if nothing happened after 1s, just show them
    const fallback = setTimeout(() => {
      targets.forEach(el => el.classList.add('in'));
    }, 1000);

    return () => {
      io.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <>
      {/* ===== Hero ===== */}
      <section id="home" className="section home">
        <div className="hero reveal">
          <div className="hero-media">
            <img
              src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=1600&auto=format"
              alt="training illustration"
            />
          </div>

          <div className="hero-card">
            <h1>Welcome to White Mountain</h1>
            <p>Turning Small Steps into Big Impact</p>
          </div>
        </div>
      </section>

      {/* ===== Introduction ===== */}
      <section id="introduction" className="section introduction-section intro-radial-grid">
        {/* Decorative SVGs — add .reveal */}
        <img
          src="/images/rightUp.svg"
          alt="Frame 2"
          className="introduction-decor-top-left reveal"
        />
        <img
          src="/images/frame-2.svg"
          alt="Frame 2"
          className="introduction-decor-bottom-right reveal"
        />

        <div className="introduction-content">
          <div className="introduction-header reveal">
            <h2 className="introduction-title">Introduction</h2>
            <div className="title-underline" />
          </div>

          <div className="introduction-text reveal">
            <p className="lead-text">
              At White Mountain, we believe that knowledge is the foundation of success.
            </p>
            <p>
              We are a leading training and development company dedicated to empowering professionals,
              organizations, and entrepreneurs across multiple industries. Our mission is simple: to
              transform skills into leadership, ideas into businesses, and potential into success stories.
            </p>
          </div>
        </div>
      </section>

      {/* ===== We Provide ===== */}
      {/* <section id="weprovide" className="section weprovide-section">
        <div className="weprovide-content">
          <div className="weprovide-header">
            <h2 className="weprovide-title">We Provide</h2>
            <div className="title-underline" />
          </div>
          <div className="weprovide-text">
            <p>
              We provide specialized training programs designed to meet the needs of individuals and
              companies across medical, financial, management, HR, dental, pharmacy, technology, and event
              management sectors—and beyond. Whether you are an individual looking to enhance your career,
              a startup founder seeking guidance, or an organization aiming to develop leaders, White Mountain
              is your partner in growth.
            </p>
          </div>
        </div>
      </section> */}

      {/* ===== We Provide (NEW) ===== */}
<section id="weprovide" className="section weprovide-section weprovide-cute">
  <div className="weprovide-bg-decor" aria-hidden="true"></div>

  <div className="weprovide-content">
    <div className="weprovide-header reveal">
      <h2 className="weprovide-title">We Provide</h2>
      <div className="title-underline" />
      <p className="weprovide-sub">
        Tailored training paths for teams and individuals — practical, modern, and impact-driven.
      </p>
    </div>

    {/* Staggered cards (cute pop-in on scroll) */}
    <div className="provide-grid">
      <article className="provide-card reveal" style={{"--i": 1}}>
        <div className="provide-icon" role="img" aria-label="Leadership">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        </div>
        <h3>Leadership & Management</h3>
        <p>Programs that grow confident decision-makers and collaborative leaders.</p>
        <span className="chip">Workshops</span>
      </article>

      <article className="provide-card reveal" style={{"--i": 2}}>
        <div className="provide-icon" role="img" aria-label="Technology">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
        </div>
        <h3>Technology Upskilling</h3>
        <p>From fundamentals to advanced tooling—stay sharp in a fast world.</p>
        <span className="chip">Hands-on</span>
      </article>

      <article className="provide-card reveal" style={{"--i": 3}}>
        <div className="provide-icon" role="img" aria-label="Healthcare">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </div>
        <h3>Healthcare Tracks</h3>
        <p>Focused training for medical, dental, and pharmacy professionals.</p>
        <span className="chip">Accurate</span>
      </article>

      <article className="provide-card reveal" style={{"--i": 4}}>
        <div className="provide-icon" role="img" aria-label="Finance">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        </div>
        <h3>Finance & Analytics</h3>
        <p>Data-driven skills for reporting, insights, and better decisions.</p>
        <span className="chip">Data</span>
      </article>

      <article className="provide-card reveal" style={{"--i": 5}}>
        <div className="provide-icon" role="img" aria-label="HR">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <h3>HR & People Ops</h3>
        <p>Culture, performance, and growth frameworks that actually work.</p>
        <span className="chip">People</span>
      </article>

      <article className="provide-card reveal" style={{"--i": 6}}>
        <div className="provide-icon" role="img" aria-label="Events">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </div>
        <h3>Events & Soft Skills</h3>
        <p>Communication, facilitation, and event execution with polish.</p>
        <span className="chip">Soft Skills</span>
      </article>
    </div>
  </div>
</section>


      {/* ===== CTA ===== */}
      <section className="section cta-section">
        <div className="cta-bg-decoration"></div>
        <div className="cta-content reveal">
          <div className="cta-icon-wrapper">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="cta-icon">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              <path d="M13 8H7"/>
              <path d="M17 12H7"/>
            </svg>
          </div>
          <h2 className="cta-title">Ready to shape your next chapter?</h2>
          <div className="cta-divider"></div>
          <p className="cta-description">
            Let's talk about how White Mountain can design a learning journey tailored to your team,
            project, or ambitions.
          </p>
          <Link className="cta-button" to="/contact">
            <span>Contact Us</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </Link>
        </div>
        <div className="cta-floating-shapes">
          <div className="cta-shape cta-shape-1"></div>
          <div className="cta-shape cta-shape-2"></div>
          <div className="cta-shape cta-shape-3"></div>
        </div>
      </section>
    </>
  );
}
